import { Injectable } from '@angular/core';
import {
  SETTINGS_SEARCH_ENTRIES,
  SETTINGS_TABS
} from '@configuration/shared/settings-search/settings-search.registry';
import {
  ISettingsSearchContext,
  ISettingsSearchEntry,
  ISettingsSearchResult,
  ISettingsSearchSegment,
  ISettingsTab
} from '@configuration/shared/settings-search/settings-search.interfaces';
import { TranslateService } from '@ngx-translate/core';

const COMBINING_MARKS = /[̀-ͯ]/g;
const HTML_TAGS = /<[^>]*>/g;
const HTML_ENTITIES: { [entity: string]: string } = {
  '&amp;': '&',
  '&nbsp;': ' ',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'"
};

/** Lower-cases and strips accents so `déconnexion` matches `deconnexion`, one character in → one character out. */
export function foldCharacter(character: string): string {
  const folded = character
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase();

  return folded.length === 1
    ? folded
    : character.toLowerCase().charAt(0) || character;
}

/** Same as `foldCharacter` over a whole string; the result keeps the input's length so match offsets stay valid. */
export function foldText(value: string): string {
  return Array.from(value ?? '')
    .map((character: string) => foldCharacter(character))
    .join('');
}

/** Turns an i18n value (may carry inline HTML) into plain text. */
export function stripHtml(value: string): string {
  return (value ?? '')
    .replace(HTML_TAGS, ' ')
    .replace(
      /&[a-z#0-9]+;/gi,
      (entity: string) => HTML_ENTITIES[entity] ?? entity
    )
    .replace(/\s+/g, ' ')
    .trim();
}

const HTML_ESCAPES: { [character: string]: string } = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

export function escapeHtml(value: string): string {
  return (value ?? '').replace(
    /[&<>"']/g,
    (character: string) => HTML_ESCAPES[character]
  );
}

/** Renders highlight segments as escaped HTML, matched runs wrapped in `<mark>`, with no whitespace between runs. */
export function segmentsToHtml(segments: ISettingsSearchSegment[]): string {
  return segments
    .map((segment: ISettingsSearchSegment) =>
      segment.isMatch
        ? `<mark>${escapeHtml(segment.text)}</mark>`
        : escapeHtml(segment.text)
    )
    .join('');
}

/** Splits a query into folded tokens (`is_idle_enabled` → `is`, `idle`, `enabled`). */
export function tokenize(query: string): string[] {
  return foldText(query ?? '')
    .split(/[\s_\-\/,.;:()"“”«»']+/)
    .filter((token: string) => token.length > 0);
}

@Injectable({ providedIn: 'root' })
export class SettingsSearchService {
  public static readonly MAX_RESULTS = 10;

  constructor(private readonly _translate: TranslateService) {}

  public get tabs(): ISettingsTab[] {
    return SETTINGS_TABS;
  }

  public get entries(): ISettingsSearchEntry[] {
    return SETTINGS_SEARCH_ENTRIES;
  }

  public isTabEnabled(
    tab: ISettingsTab,
    context: ISettingsSearchContext
  ): boolean {
    return !tab.isHidden?.(context) && !tab.isDisabled(context);
  }

  public isEntryVisible(
    entry: ISettingsSearchEntry,
    context: ISettingsSearchContext,
    tabs: ISettingsTab[] = this.tabs
  ): boolean {
    const tab = tabs.find((t: ISettingsTab) => t.url === entry.tab);

    if (!tab || !this.isTabEnabled(tab, context)) {
      return false;
    }

    return entry.isVisible ? !!entry.isVisible(context) : true;
  }

  /**
   * Ranks the settings the firm can see against `query` and returns the best
   * `MAX_RESULTS`. Every token of the query has to match somewhere (title,
   * description, tab label or keywords); title matches rank first.
   */
  public search(
    query: string,
    context: ISettingsSearchContext,
    entries: ISettingsSearchEntry[] = this.entries,
    tabs: ISettingsTab[] = this.tabs
  ): ISettingsSearchResult[] {
    const tokens = tokenize(query);

    if (tokens.length === 0) {
      return [];
    }

    const results: ISettingsSearchResult[] = [];

    entries.forEach((entry: ISettingsSearchEntry, index: number) => {
      if (!this.isEntryVisible(entry, context, tabs)) {
        return;
      }

      const tab = tabs.find(
        (t: ISettingsTab) => t.url === entry.tab
      ) as ISettingsTab;
      const title = this.translate(entry.titleKey) || this.humanize(entry.id);
      const description = entry.descriptionKey
        ? this.translate(entry.descriptionKey)
        : '';
      const tabLabel = this.translate(tab.label) || tab.label;
      const keywords = foldText(
        [entry.id, ...(entry.keywords ?? [])].join(' ').replace(/[_\-]+/g, ' ')
      );

      const foldedTitle = foldText(title);
      const foldedDescription = foldText(description);
      const foldedTabLabel = foldText(tabLabel);

      let score = 0;

      for (const token of tokens) {
        const tokenScore = Math.max(
          this.scoreField(foldedTitle, token, 5, 4, 3),
          this.scoreField(foldedDescription, token, 2, 2, 1),
          this.scoreField(`${foldedTabLabel} ${keywords}`, token, 0.5, 0.5, 0.5)
        );

        if (tokenScore === 0) {
          return;
        }

        score += tokenScore;
      }

      results.push({
        entry,
        tab,
        title,
        titleSegments: this.segments(title, foldedTitle, tokens),
        description,
        tabLabel,
        score: score + (entries.length - index) / (entries.length * 1000)
      });
    });

    return results
      .sort(
        (a: ISettingsSearchResult, b: ISettingsSearchResult) =>
          b.score - a.score
      )
      .slice(0, SettingsSearchService.MAX_RESULTS);
  }

  private translate(key: string): string {
    const value = this._translate.instant(key);

    return typeof value === 'string' && value !== key ? stripHtml(value) : '';
  }

  private humanize(id: string): string {
    return id.replace(/^is_/, '').replace(/[_\-]+/g, ' ');
  }

  /** A token at the very start of the field beats one starting a later word, which beats one inside a word. */
  private scoreField(
    field: string,
    token: string,
    prefixScore: number,
    wordStartScore: number,
    containsScore: number
  ): number {
    const index = field.indexOf(token);

    if (index < 0) {
      return 0;
    }

    if (index === 0) {
      return prefixScore;
    }

    return /\s/.test(field.charAt(index - 1)) ? wordStartScore : containsScore;
  }

  private segments(
    title: string,
    foldedTitle: string,
    tokens: string[]
  ): ISettingsSearchSegment[] {
    const matched: boolean[] = new Array(title.length).fill(false);

    for (const token of tokens) {
      let from = 0;
      let index = foldedTitle.indexOf(token, from);

      while (index >= 0) {
        for (let i = index; i < index + token.length; i++) {
          matched[i] = true;
        }

        from = index + token.length;
        index = foldedTitle.indexOf(token, from);
      }
    }

    const segments: ISettingsSearchSegment[] = [];

    for (let i = 0; i < title.length; i++) {
      const last = segments[segments.length - 1];

      if (last && last.isMatch === matched[i]) {
        last.text += title.charAt(i);
      } else {
        segments.push({ text: title.charAt(i), isMatch: matched[i] });
      }
    }

    return segments;
  }
}
